import { Link, useParams } from "react-router-dom";

import { useGetQuizQuestionDetailsQuery } from "../../slices/quizQuestionApiSlice";

const QuizQuestionDetailsScreen = () => {
  const { id } = useParams();

  const {
    data: quizQuestion,
    isLoading,
    error,
  } = useGetQuizQuestionDetailsQuery(id);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link
          to="/quizzes"
          className="inline-flex items-center gap-2 mb-6 rounded-xl bg-white px-4 py-2 text-blue-600 shadow hover:bg-blue-50 transition"
        >
          ← Back to Quizzes
        </Link>

        {/* Loading */}
        {isLoading && (
          <div className="rounded-xl bg-blue-100 p-4 text-center text-blue-700">
            Loading quiz question details...
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-xl bg-red-100 p-4 text-center text-red-700">
            {error?.data?.message ||
              error?.error ||
              "Failed to load quiz question details"}
          </div>
        )}

        {/* Details */}
        {!isLoading && quizQuestion && (
          <div className="overflow-hidden rounded-3xl border border-white/30 bg-white/80 shadow-2xl backdrop-blur-lg">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-8 text-white">
              <h1 className="text-3xl font-bold">Quiz Question Mapping</h1>

              <p className="mt-2 text-blue-100">
                Question assignment details for this quiz
              </p>
            </div>

            <div className="space-y-8 p-8">
              {/* Quiz Information */}
              <div>
                <h2 className="mb-4 text-lg font-bold text-gray-800">
                  Quiz Information
                </h2>

                <div className="rounded-2xl border bg-gray-50 p-5">
                  <p className="text-sm text-gray-500">Quiz ID</p>

                  <p className="font-semibold text-gray-800">
                    {quizQuestion.quiz?._id || quizQuestion.quiz}
                  </p>
                </div>
              </div>

              {/* Question Information */}
              <div>
                <h2 className="mb-4 text-lg font-bold text-gray-800">
                  Question Information
                </h2>

                <div className="rounded-2xl border bg-gray-50 p-5 space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Question</p>

                    <p className="text-lg font-semibold text-gray-800">
                      {quizQuestion.question?.questionText ||
                        quizQuestion.question?.text ||
                        quizQuestion.question?.title ||
                        "Question unavailable"}
                    </p>
                  </div>

                  {quizQuestion.question?.options && (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Options</p>

                      <ul className="space-y-2">
                        {quizQuestion.question.options.map((option, index) => (
                          <li
                            key={index}
                            className="rounded-lg bg-white border px-4 py-2 text-gray-700"
                          >
                            {option}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Mapping Details */}

              <div>
                <h2 className="mb-4 text-lg font-bold text-gray-800">
                  Mapping Details
                </h2>

                <div className="grid gap-6 md:grid-cols-3">
                  <div className="rounded-2xl border bg-gray-50 p-5">
                    <p className="text-sm text-gray-500">Order</p>

                    <p className="mt-1 text-xl font-bold text-gray-800">
                      {quizQuestion.order}
                    </p>
                  </div>

                  <div className="rounded-2xl border bg-gray-50 p-5">
                    <p className="text-sm text-gray-500">Marks</p>

                    <p className="mt-1 text-xl font-bold text-gray-800">
                      {quizQuestion.marks}
                    </p>
                  </div>

                  <div className="rounded-2xl border bg-gray-50 p-5">
                    <p className="text-sm text-gray-500">Status</p>

                    <span
                      className={`inline-flex mt-2 rounded-full px-4 py-1 text-sm font-semibold ${
                        quizQuestion.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {quizQuestion.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Created By */}

              <div>
                <h2 className="mb-4 text-lg font-bold text-gray-800">
                  Created By
                </h2>

                <div className="rounded-2xl border bg-gray-50 p-5">
                  <p className="font-semibold text-gray-800">
                    {quizQuestion.createdBy?.name || "-"}
                  </p>

                  <p className="text-gray-500">
                    {quizQuestion.createdBy?.phone || ""}
                  </p>
                </div>
              </div>

              {/* Buttons */}

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  to={`/quiz-questions/${quizQuestion._id}/edit`}
                  className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3 text-center font-semibold text-white shadow-lg hover:from-blue-700 hover:to-indigo-700 transition"
                >
                  Edit Mapping
                </Link>

                <Link
                  to="/quizzes"
                  className="flex-1 rounded-xl border border-gray-300 bg-white py-3 text-center font-semibold text-gray-700 hover:bg-gray-100 transition"
                >
                  Back
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizQuestionDetailsScreen;
